import Script from "next/script";
import React, { useEffect, useRef } from "react";
import settings, { ENV_NAMES } from "../../settings";
const { GAME_UID, APP_ENV } = settings;
const { PROD } = ENV_NAMES;

interface AdLibrary {
  [adId: string]: {
    sizes: [number, number][];
    mapping: {
      [breakpoint: number]: number[];
    };
  };
}
const adSizes: AdLibrary = {
  ad_driver_modal_01: {
    sizes: [
      [98, 37],
      [122, 46],
    ],
    mapping: {
      0: [98, 37],
      936: [122, 46],
    },
  },
  ad_driver_modal_02: {
    sizes: [
      [342, 44],
      [468, 60],
    ],
    mapping: {
      0: [342, 44],
      936: [468, 60],
    },
  },
  ad_bnr_atf_01: {
    sizes: [
      [320, 50],
      [728, 90],
    ],
    mapping: {
      0: [320, 50],
      800: [728, 90],
    },
  },
  ad_presented_by_01: {
    sizes: [
      [216, 56],
      [116, 30],
    ],
    mapping: {
      0: [116, 30],
      800: [216, 56],
    },
  },
  ad_interstitial_01: {
    sizes: [[300, 250]],
    mapping: {
      0: [300, 250],
    },
  },
};

/**
 * Component to render GPT (Google Publisher Tag) ads to the DOM.
 */
export default function AdUnit({ id }: { id: string }) {
  const shouldLogRenders = APP_ENV !== PROD && false; // * toggle logging with this conditional by changing && false.

  const timeOutMs = useRef(50);
  const checkReadiness = React.useCallback((): Promise<void> => {
    if (typeof window !== "undefined" && window.googletag && window.googletag.apiReady) {
      timeOutMs.current = 50;
      return Promise.resolve();
    } else {
      timeOutMs.current *= 1.25;
      console.warn("GPT | Debouncing for script initialization...");
      return new Promise((resolve) => setTimeout(resolve, timeOutMs.current)).then(checkReadiness);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      checkReadiness().then(() => {
        const googletag = window.googletag;
        googletag?.cmd.push(function () {
          const mapping = adSizes[id].mapping;
          const sizes = adSizes[id].sizes;
          const sizeMapping = googletag?.sizeMapping?.();

          Object.keys(mapping).forEach((breakpoint) => {
            sizeMapping.addSize([Number(breakpoint), 0], [mapping[breakpoint]]);
          });
          const builtMapping = sizeMapping.build();

          const slot = googletag
            .defineSlot(`/73074296/NASCAR-web/fantasy/grid-challenge`, sizes, id)
            .addService(googletag.pubads())
            .setTargeting("pos", id)
            .defineSizeMapping(builtMapping);
          if (shouldLogRenders) {
            googletag.pubads().addEventListener("slotRenderEnded", function (event) {
              if (event.slot === slot) {
                console.debug("GPT | showing: ", event.slot.getSlotElementId());
              }
            });
          }
          googletag.pubads().disableInitialLoad();
          googletag.enableServices();
          googletag.display(id);
        });
        googletag.cmd.push(function () {
          const slot = googletag
            .pubads()
            .getSlots()
            .find((s) => s.getSlotElementId() === id);
          googletag.pubads().refresh([slot]);
        });
      });

      return () => {
        if (typeof window !== "undefined" && window?.googletag?.pubads) {
          const googletag = window.googletag;
          const slot = googletag
            .pubads()
            .getSlots()
            .find((s) => s.getSlotElementId() === id);
          googletag.cmd.push(function () {
            if (shouldLogRenders) console.debug(`GPT | destroying: `, id);
            googletag.destroySlots([slot]);
          });
        }
      };
    }
  }, [id, checkReadiness, shouldLogRenders]);

  return <div id={id} />;
}

export const AdScripts = React.memo(() => {
  return (
    <>
      <Script
        id={`nascar_${GAME_UID}-ad-script-1`}
        key={`nascar_${GAME_UID}-ad-script-1`}
        src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
        strategy={"beforeInteractive"}
        async={true}
      />
      <Script
        id={`nascar_${GAME_UID}-ad-script-2`}
        key={`nascar_${GAME_UID}-ad-script-2`}
        strategy={"afterInteractive"}
      >
        {`window.googletag = window.googletag || { cmd: [] };`}
      </Script>
    </>
  );
});
